import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageBar from '../../components/PageBar';
import PageLoading from '../../components/PageLoading';
import ListGrid from '../../components/ListGrid';
import { useToast } from '../../components/ToastContext';
import {
  loadSpellings, loadVendors, loadByVendor, setMapping, addVendor, tally,
  stateOf, STATE_WORDS, MAPPED, WAITING, RETIRED,
} from './vendorMappingData';

/*
  SETTINGS -> VENDOR MAPPING.

  **Every spelling MedEx's two systems use for a vendor, pointed at one real
  vendor.** ClaimSoft uses 94 spellings and Account Edge 363, against 28 real
  vendors in the catalog. A spelling nothing maps makes every item under it
  unmatchable, which was 9,892 items when this was built on 3 October 2026.

  **It changes nothing your systems said.** The raw tables keep every spelling
  exactly as it arrived. A mapping is a separate row, and removing one leaves the
  underlying data untouched. The owner asked about this before it was built and
  it is the reason the screen is safe to work in.

  **Retired spellings are flagged, never mapped.** Account Edge sinks a dead
  vendor by prefixing z's or writing "do not use" into its name. Both say
  somebody decided to stop using it, and mapping its items onto the live vendor
  would quietly undo that decision. His instruction, same day.

  **The order is biggest first**, because the work is wildly uneven: on the day
  it was built ten spellings covered half of everything outstanding and the long
  tail was suppliers bought from once.
*/

const FILTERS = [
  { key: WAITING, label: 'Waiting on you' },
  { key: MAPPED,  label: 'Mapped' },
  { key: RETIRED, label: 'Retired' },
  { key: 'all',   label: 'All' },
];

const SYSTEM_WORDS = { claimsoft: 'ClaimSoft', account_edge: 'Account Edge' };

/*
  **Two views of one thing.** By spelling is where the work happens; by vendor is
  where it gets checked, and it is the only place the ClaimSoft to Account Edge
  link is visible at all, because that link is produced rather than stored.

  It matters more than a convenience. The owner described the Orders workflow on
  3 October 2026: ClaimSoft data in, manipulated, out as Account Edge data, back
  in, and out again as ClaimSoft data. **That round trip only works if both sides
  point at the same vendor**, and this is the view where a wrong one shows up.
*/
const VIEWS = [
  { key: 'spellings', label: 'By spelling' },
  { key: 'vendors',   label: 'By vendor' },
];

export default function VendorMapping() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState(WAITING);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(null);
  const [failed, setFailed] = useState(null);
  const [view, setView] = useState('spellings');
  const [byVendor, setByVendor] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([loadSpellings(), loadVendors()]);
    /*
      **A read that fails is not a list that is empty**, and drawing one as the
      other is this project's oldest fault. An error says so and offers another
      go; it does not draw "nothing to map" over a network blip.
    */
    if (a.error || b.error) { setFailed((a.error || b.error).message); setRows([]); return; }
    setFailed(null);
    setRows(a.rows);
    setVendors(b.vendors);
  }, []);

  useEffect(() => { load(); }, [load]);

  /* The vendor view is read when it is first opened, and again after a mapping
     changes, because a mapping is exactly what it is showing. */
  useEffect(() => {
    if (view !== 'vendors') return;
    let alive = true;
    loadByVendor().then(r => { if (alive) setByVendor(r.error ? [] : r.rows); });
    return () => { alive = false; };
  }, [view, rows]);

  const counts = useMemo(() => (rows ? tally(rows) : null), [rows]);

  const shown = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filter !== 'all' && stateOf(r) !== filter) return false;
      if (!q) return true;
      return r.spelling.toLowerCase().includes(q) || (r.vendor_name || '').toLowerCase().includes(q);
    });
  }, [rows, filter, search]);

  /* Changing one row changes one row. The list is not reloaded, because
     re-reading 457 rows to reflect one answer makes the screen feel slow and
     loses the place the person was working. */
  const apply = useCallback(async (row, vendorId) => {
    setBusy(row.spelling_key);
    const res = await setMapping(row, vendorId);
    setBusy(null);
    if (res.error) { toast.error('Could not save that: ' + res.error.message); return; }
    if (!res.ok) { toast.error('That did not save. Nothing was changed.'); return; }
    const vendor = vendors.find(v => v.id === vendorId) || null;
    setRows(prev => prev.map(r => r.spelling_key === row.spelling_key
      ? { ...r, vendor_id: vendorId || null, vendor_name: vendor ? vendor.name : null, vendor_name_id: res.vendorNameId || null }
      : r));
    toast.success(vendorId
      ? '"' + row.spelling + '" is ' + (vendor ? vendor.name : 'mapped')
      : '"' + row.spelling + '" is no longer mapped');
  }, [vendors, toast]);

  const onNewVendor = useCallback(async (row) => {
    const name = window.prompt('What is this vendor called?', row.spelling);
    if (name === null) return;
    const res = await addVendor(name);
    if (res.error) { toast.error('Could not add that vendor: ' + res.error.message); return; }
    if (!res.ok || !res.vendor) { toast.error('That vendor was not added. Nothing was changed.'); return; }
    setVendors(prev => [...prev, res.vendor].sort((a, b) => a.name.localeCompare(b.name)));
    await apply(row, res.vendor.id);
  }, [apply, toast]);

  if (rows === null) return <><PageBar title="Vendor Mapping" /><PageLoading /></>;

  const columns = [
    { key: 'spelling', label: 'Spelling your system uses', width: '2fr' },
    { key: 'system',   label: 'From',   width: '140px' },
    { key: 'items',    label: 'Items',  width: '90px', align: 'right' },
    { key: 'vendor',   label: 'Points at', width: '2fr' },
  ];

  const cells = r => {
    const state = stateOf(r);
    return [
      <span>
        <span className="rpnum">{r.spelling}</span>
        {state === RETIRED && (
          <span className="oh-pill-soon" style={{ marginLeft: 8 }} title={STATE_WORDS[RETIRED].says}>Retired</span>
        )}
      </span>,
      SYSTEM_WORDS[r.system] || r.system,
      r.items.toLocaleString(),
      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          className="fld"
          style={{ maxWidth: 240 }}
          value={r.vendor_id || ''}
          disabled={busy === r.spelling_key}
          onChange={e => apply(r, e.target.value || null)}
          aria-label={'Which vendor is "' + r.spelling + '"'}
        >
          <option value="">Not mapped</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <button type="button" className="btn-quiet" disabled={busy === r.spelling_key}
          onClick={() => onNewVendor(r)}>
          New vendor
        </button>
      </span>,
    ];
  };

  return (
    <>
      <PageBar
        title="Vendor Mapping"
        subtitle="Every spelling your systems use for a vendor, pointed at one real vendor"
        subTabs={VIEWS}
        activeSubTab={view}
        onSubTabChange={setView}
        meta={counts && (
          <>
            <span><b>{counts.waiting}</b> waiting on you</span>
            <span><b>{counts.mapped}</b> mapped</span>
            <span><b>{counts.retired}</b> retired</span>
            <span><b>{counts.itemsWaiting.toLocaleString()}</b> items still unmatchable</span>
          </>
        )}
        controls={
          <>
            <input className="tb-search" placeholder={view === 'vendors' ? 'Search a vendor' : 'Search a spelling or a vendor'}
              value={search} onChange={e => setSearch(e.target.value)} />
            {view === 'spellings' && (
              <select className="tb-select" value={filter} onChange={e => setFilter(e.target.value)}
                aria-label="Which spellings to show">
                {FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            )}
          </>
        }
      />

      {view === 'vendors' ? (
        byVendor === null ? <PageLoading /> : (
          <ListGrid
            columns={[
              { key: 'vendor', label: 'Vendor', width: '1.2fr' },
              { key: 'cs',     label: 'ClaimSoft calls it', width: '2fr' },
              { key: 'ae',     label: 'Account Edge calls it', width: '2fr' },
              { key: 'items',  label: 'Items', width: '90px', align: 'right' },
              { key: 'cat',    label: 'In the catalog', width: '120px', align: 'right' },
            ]}
            rows={byVendor.filter(r => !search.trim()
              || r.vendor_name.toLowerCase().includes(search.trim().toLowerCase()))}
            keyOf={r => r.vendor_id}
            cells={r => [
              <span>
                <span className="rpnum">{r.vendor_name}</span>
                {/* **Named in one system and not the other.** Either genuinely
                    one-sided, or a spelling still waiting in the other system.
                    It caught Ottobock the day it was built. */}
                {r.in_one_system_only && (
                  <span className="oh-pill-soon" style={{ marginLeft: 8 }}
                    title="This vendor is named in one of your systems and not the other. Either that is right, or a spelling in the other system is still waiting.">
                    One system only
                  </span>
                )}
              </span>,
              r.claimsoft_spellings || <span style={{ color: 'var(--text-secondary)' }}>nothing yet</span>,
              r.account_edge_spellings || <span style={{ color: 'var(--text-secondary)' }}>nothing yet</span>,
              Number(r.items).toLocaleString(),
              Number(r.products_in_the_catalog).toLocaleString(),
            ]}
            layoutKey="vendor-mapping-by-vendor"
            empty="No vendor matches that."
          />
        )
      ) : failed ? (
        <div className="card" style={{ padding: 24 }}>
          <p>OnHand could not read the vendor spellings just now.</p>
          <p style={{ color: 'var(--text-secondary)' }}>{failed}</p>
          <button type="button" className="btn" onClick={load}>Try again</button>
        </div>
      ) : (
        <ListGrid
          columns={columns}
          rows={shown}
          /*
            **The system has to be in the key.** A spelling can be in both
            systems, and three are: AMERICAN DIAGNOSTIC CORPORATION, EBI LLC and
            PWMINOR. Keying on the spelling alone put two rows under one key,
            which React warns about and which lets it draw one and drop the
            other.

            **Updating state still matches on the spelling alone, deliberately.**
            A mapping belongs to the spelling rather than to the system, so
            mapping it in one place maps it in the other, and both rows should
            show that at once.
          */
          /*
            **The key is the system and the spelling AS WRITTEN**, not the
            normalised one. Two goes at this were wrong. The spelling alone
            collides because three spellings are in both systems. The system plus
            the normalised spelling still collides, because Account Edge holds
            both "Knit-Rite" and "Knit Rite" and they normalise to the same thing.
            They are two rows a person has to answer separately, so they need two
            keys.
          */
          keyOf={r => r.system + '|' + r.spelling}
          cells={cells}
          layoutKey="vendor-mapping"
          empty={
            filter === WAITING
              ? 'Nothing is waiting. Every spelling your systems use points at a vendor.'
              : 'No spelling matches that.'
          }
        />
      )}
    </>
  );
}
